import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const assetUrl = (url) => url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null;
const CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$' };
const cs = (code) => CURRENCY_SYMBOLS[code] || code + ' ';
const TF_LOGO = "https://www.targetfunder.com/tf_logo.png";

export default function TermsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);

  useEffect(() => {
    axios.get(`${API}/clubs/${slug}`).then(r => setClub(r.data)).catch(() => {});
  }, [slug]);

  if (!club) return <div className="min-h-screen flex items-center justify-center text-[#1D3557]/40">Loading...</div>;

  const fee = `${cs(club.currency)}${club.entry_fee?.toFixed(2)}`;
  const prizeList = club.prizes?.length
    ? club.prizes.map(p => `${p.position}: ${p.description}`).join('; ')
    : 'As advertised on the club competition page';

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#1D3557]/5 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {club.logo_url && (
              <img src={assetUrl(club.logo_url)} alt={club.name} className="w-8 h-8 object-contain" />
            )}
            <h1 className="text-lg font-bold text-[#1D3557]">{club.name}</h1>
          </div>
          <button
            onClick={() => navigate(`/worldcup2026/${slug}`)}
            className="text-xs font-bold uppercase tracking-widest text-[#1D3557]/40 hover:text-[#1D3557] transition-colors"
            data-testid="back-to-club-btn"
          >
            Back to Predictor
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="terms-content">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E63946] mb-3">Legal</p>
        <h2 className="font-black text-3xl sm:text-4xl uppercase tracking-tight leading-none text-[#1D3557] mb-8">
          Terms and Conditions
        </h2>
        
        <div className="text-[#1D3557]/55 space-y-6 text-sm leading-relaxed">
          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">The Promoter</h3>
            <p>This fundraiser is organised and operated by {club.name} (the &ldquo;Club&rdquo;). TargetFunder provides the technical platform and hosting services but is not the promoter and bears no responsibility for the management of the competition or prize fulfilment.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Platform Operator</h3>
            <p>TargetFunder (targetfunder.com) is owned and operated by Castleview Resources Ltd, Unit 3 Grange Road Retail Park, Dublin 16, D16 XD82, Ireland. VAT ID: IE4271347SH.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Eligibility</h3>
            <p>Entry is open to residents of the United Kingdom and the Republic of Ireland aged 18 years or over. The Club reserves the right to request proof of age and residency before awarding any prize.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Entry Fees</h3>
            {club.pricing_model === 'per_entry' ? (
              <p>The entry fee is {fee} per submission. Payments are processed securely via Stripe and go to the Club to support its fundraiser, less Stripe payment processing fees and a small per-entry platform fee retained by TargetFunder. All entry fees are non-refundable once the competition begins on 11 June 2026.</p>
            ) : (
              <p>The entry fee is {fee} per submission. Payments are processed securely via Stripe and go directly to the Club. All entry fees are non-refundable once the competition begins on 11 June 2026.</p>
            )}
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">The &ldquo;Unique Combination&rdquo; Rule</h3>
            <p>Each specific combination of six teams can only be selected once per Club. Entries are accepted on a &ldquo;first-come, first-served&rdquo; basis. If your preferred combination has already been selected, you must choose a different set of teams.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Nature of the Competition</h3>
            <p>This is a Prize Competition based on skill, judgement, and knowledge. Points are awarded based on the real-world performance of your selected teams during the World Cup 2026. This is not a lottery or a game of chance.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Prizes</h3>
            <p>{prizeList}. The Club is solely responsible for the provision and distribution of prizes. If a tie occurs on the leaderboard, the total goals scored by the tied players' selected teams (excluding penalty shootouts) will be used as a tiebreaker.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Closing Date</h3>
            <p>All entries must be completed and paid for by 07:00 AM GMT on 11 June 2026. No entries will be accepted after this time.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Liability</h3>
            <p>To the extent permitted by law, the Club and TargetFunder exclude liability for any loss or damage arising from technical errors, network failures, or the cancellation/postponement of tournament matches.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-2">Platform Operator&rsquo;s Role</h3>
            <p>TargetFunder facilitates the sale of entries on behalf of the Club via the Stripe payment platform. TargetFunder is not the organiser of the competition and is not responsible for the selection of winners, the awarding of prizes, or the delivery of prizes &mdash; the Club is solely responsible.</p>
          </div>
        </div>
      </main>

      <footer className="py-6 px-6 border-t border-[#1D3557]/5">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-3">
          <p className="text-[#1D3557]/30 text-sm">Supporting {club.name} through World Cup 2026</p>
          <a
            href="https://www.targetfunder.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#F8FAFA] border border-[#1D3557]/5 hover:border-[#1D3557]/15 transition-colors"
            data-testid="targetfunder-badge"
          >
            <img src={TF_LOGO} alt="TargetFunder" className="h-5 w-5 object-contain" />
            <span className="font-heading font-black text-sm uppercase tracking-tighter leading-none text-[#1D3557]/40">
              Made by <span className="text-[#1D3557]">target</span><span className="text-[#E63946]">funder</span><span className="text-[#1D3557]/30">.com</span>
            </span>
          </a>
        </div>
      </footer>
    </div>
  );
}
